import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { RegionProvider } from "@/context/RegionContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { MarketPulse } from "@/components/MarketPulse";
import AuthCallback from "@/components/AuthCallback";

import Home from "@/pages/Home";
import News from "@/pages/News";
import Research from "@/pages/Research";
import ArticleDetail from "@/pages/ArticleDetail";
import Conviction from "@/pages/Conviction";
import ConvictionDetail from "@/pages/ConvictionDetail";
import ConvictionV2List from "@/pages/ConvictionV2List";
import ConvictionV2Detail from "@/pages/ConvictionV2Detail";
import { FlagSwitch } from "@/components/conviction/shared";
import CalendarPage from "@/pages/Calendar";
import Pricing from "@/pages/Pricing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import CheckEmail from "@/pages/CheckEmail";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import CompleteProfile from "@/pages/CompleteProfile";
import Admin from "@/pages/Admin";
import Analysts from "@/pages/Analysts";
import AnalystDetail from "@/pages/AnalystDetail";
import Account from "@/pages/Account";
import Checkout from "@/pages/Checkout";
import Membership from "@/pages/Membership";
import PublicProfile from "@/pages/PublicProfile";
import { NicknamePrompt } from "@/components/wallet/NicknamePrompt";

// Redirect deprecated public routes while preserving any query string (no loops).
function Redirect({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search || ""}`} replace />;
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function CaptureReferral() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) localStorage.setItem("cl_ref", ref.trim().toUpperCase());
  }, []);
  return null;
}

function Shell() {
  const location = useLocation();
  // Process Google OAuth callback FIRST (session_id in URL fragment).
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      {!isAdminRoute && <Navbar />}
      {!isAdminRoute && <MarketPulse />}
      <main className="min-h-[60vh]">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/news" element={<News />} />
          <Route path="/research" element={<Research />} />
          <Route path="/alpha" element={<Redirect to="/research" />} />
          <Route path="/article/:slug" element={<ArticleDetail />} />
          <Route path="/conviction" element={<FlagSwitch v2={<ConvictionV2List />} legacy={<Conviction />} />} />
          <Route path="/conviction/:slug" element={<FlagSwitch v2={<ConvictionV2Detail />} legacy={<ConvictionDetail />} />} />
          <Route path="/smart-money" element={<Redirect to="/research" />} />
          <Route path="/sea" element={<Redirect to="/news" />} />
          <Route path="/indonesia" element={<Redirect to="/news" />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/analysts" element={<Analysts />} />
          <Route path="/analysts/category/:cat" element={<Analysts />} />
          <Route path="/analyst/:slug" element={<AnalystDetail />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/check-email" element={<CheckEmail />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/complete-profile" element={<CompleteProfile />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/account" element={<Account />} />
          <Route path="/checkout/:productId" element={<Checkout />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/checkout" element={<Navigate to="/membership" replace />} />
          <Route path="/u/:nickname" element={<PublicProfile />} />
        </Routes>
      </main>
      {!isAdminRoute && <Footer />}
      <NicknamePrompt />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ThemeProvider>
          <LanguageProvider>
            <AuthProvider>
              <RegionProvider>
                <ScrollTop />
                <CaptureReferral />
                <Shell />
                <Toaster theme="dark" position="top-right" richColors />
              </RegionProvider>
            </AuthProvider>
          </LanguageProvider>
        </ThemeProvider>
      </BrowserRouter>
    </div>
  );
}
